import type { CopilotLengthUnit } from "@CommonApi/merchflows/_merchflowId_/config-copilot";

/* Every `*Cm` length in the Studio's types is in raw planogram units, unscaled: the system is
   unitless end to end and the suffix is historical (Wendy, 2026-10-29). */

/** What a category's lengths are in: its raw numbers are hundredths of this unit. */
export type LengthUnit = CopilotLengthUnit;

/** Raw planogram units as the screen labels them: raw / 100 in the category's unit (absent,
 *  inches). A display label only, never fed back into a comparison. */
export const lengthLabel = (units: number, unit: LengthUnit = "in"): string =>
  `${Math.round(units) / 100} ${unit}`;

/** The label's number, whole, for copy that says "a 6 in gap" in the category's unit. */
export const wholeLength = (units: number): number => Math.round(units / 100);

/** `lengthLabel` in inches, for callers that have no category unit to hand. */
export const inchesLabel = (units: number): string => lengthLabel(units, "in");

/** `wholeLength`, under the name inch-only callers know it by. */
export const wholeInches = wholeLength;

export interface StudioPlacement {
  run: number;
  shelf: number;
  /** The 1-based bay the source authored this placement into; absent when the source
   *  has no bays. Wall readers prefer it over dividing `xCm` by the bay width, because
   *  a merged run re-derives x and can render an item inside its neighbour's span. */
  bay?: number;
  xCm: number;
  widthCm: number;
  hFacings: number;
  /** The height the product stands to on its board: rows stacked plus any top caps. Absent where
   *  it hangs or the source does not say, and then its vertical air is not graded. */
  stackHeightCm?: number;
  /** A pegboard hang line: a row for blocking, never a shelf rank to emit. */
  pegboard?: true;
  /** A rack board's placement as it really stands (DEV-3717): `xCm` gives it a slice of its
   *  host's row; this keeps the board, counted top down, and its true x for block shape. */
  rackBoard?: StudioRackBoard;
  /** 1-based board of its bay, set where several boards share its cell (a rack on its host):
   *  a MUST_HAVE on one board is kept only there. */
  board?: number;
}

export interface StudioRackBoard {
  level: number;
  levels: number;
  xCm: number;
  widthCm: number;
  /** The rack's live span along the wall. */
  fromCm: number;
  toCm: number;
  /** The height of the board's row cluster: a shelf's `yCm` where the board lines up with it. */
  rowYCm: number;
}

export interface StudioItem {
  skuId: string;
  itemMasterId?: string;
  upc: string;
  name: string;
  run: number;
  facingWidthCm: number;
  heightCm: number;
  facings: number;
  /** Attribute values keyed by spine attribute; null = unknown. */
  attrs: Record<string, string | null>;
  /** Keys of `attrs` supplied by the item-master overlay (including a semantic attribute
   *  a section mapping filled from an overlay column), in the master's column order.
   *  When present it is the whole candidate pool for level inference; absent when the
   *  item was built from the POG's columns alone. */
  overlayAttrs?: readonly string[];
  dosDays?: number;
  dosPerFacing?: number;
  maxFacings?: number;
  salesAmount?: number;
  /** Absent leaves the weight ordering rule inert. */
  weightKg?: number;
  placements: readonly StudioPlacement[];
  /** Rank the hierarchy gives this item at each level, keyed by attribute; absent when unranked. */
  priorities?: Readonly<Record<string, number>>;
}

/** A stretch of CONSECUTIVE bays that all carry this shelf row, with the row's x window across
 *  them. A ragged wall leaves a row missing from a middle bay, which splits the row into two
 *  spans: air between them is the fixture's, not the plan's, so fullness must not charge for it. */
export interface StudioShelfSpan {
  /** Left edge of the span, measured from the run's left edge. */
  xCm: number;
  widthCm: number;
  /** 1-based bay numbers, ascending and consecutive. */
  bays: readonly number[];
}

export interface StudioShelfGeometry {
  rank: number;
  yCm: number;
  thicknessCm: number;
  /** The bays that carry this row, grouped into consecutive spans. Absent for sources with no
   *  bays, where the row runs the whole width. */
  spans?: readonly StudioShelfSpan[];
  /** A pegboard hang line (`yCm` is the line): a row for blocking, never a shelf rank to emit. */
  pegboard?: true;
  /** Where the row's boards stand at different heights from bay to bay (notched bays: `yCm` is
   *  their median), each bay's own board, by 1-based bay number. The clear height a product
   *  has on the row is read bay by bay off these (`rowWindowsOf`). Absent: every bay's board
   *  stands at `yCm`, `thicknessCm` thick. */
  bayBoards?: readonly { bay: number; y: number; thickness: number }[];
  /** Strips of those boards no product may occupy (a pole), from the run's left edge: neither
   *  air nor a gap in a block. */
  deadSpans?: readonly { xCm: number; widthCm: number }[];
}

export interface StudioRunGeometry {
  run: number;
  name: string;
  widthCm: number;
  heightCm: number;
  bayWidthCm: number;
  bays: number;
  shelves: readonly StudioShelfGeometry[];
}

export type Orientation = "horizontal" | "vertical";

export interface SpineLevel {
  attr: string;
  label: string;
  orient: Orientation;
}

export interface AppliedPromotion {
  attr: string;
  parentValue: string;
  bandVariant: string;
  newValues: readonly string[];
  skuIds: readonly string[];
  previousValueOrder?: readonly string[];
  previousRootOrder?: readonly string[];
}

export interface StratumPlan {
  cdt0: string;
  section: string;

  bandAttr: string | null;
  bandValue: string | null;

  homeShelves: readonly number[];
  zone: number;
  fullHeight: boolean;

  anchors: boolean;
  skuIds: readonly string[];
  /** Anchor on the first bay the stratum itself stands in rather than its
   *  zone's first bay: a stacked brand belongs where the wall put it. */
  anchorOwnBay?: boolean;
  /** A stacked-brand sub-stratum: the band it was carved out of. It is a slab
   *  INSIDE that band's rows, not a row band of the section's cake in its own
   *  right, and the materializer derives its rows from the parent's. */
  parentCdt0?: string;
}

export interface AttributeCandidate {
  attr: string;
  label: string;
}

/** Inference, Finalize's tree plan, the measured bands and the rule set all read the wall
 *  blocking off the draft; none of them measures the wall again on the draft's retags. */
export type WallBlockingKind = "vertical" | "horizontal";

/** How the base pog blocks, read ONCE by inference (`horizontalBlocking.ts`) on the
 *  merchant's own values, on every wall that has a level to read it by. The merchant can
 *  flip it in the studio; everything downstream follows `blocking`. */
export interface WallBlocking {
  /** What every step downstream follows: inference's reading, or the merchant's choice. */
  blocking: WallBlockingKind;
  /** What inference read off the base pog. `blocking !== read` is the merchant's override. */
  read: WallBlockingKind;
  /** The level whose values are the branches (brand). */
  branchAttr: string;
  /** Its column score on the wall. */
  branchScore: number;
  /** SKU share of the voting branches that read horizontal. */
  share: number;
  /** Branch value, as the base pog names it -> horizontally blocked inside. */
  branches: Readonly<Record<string, boolean>>;
  /** More than one branch: only then can the base pog read as rows at all. */
  eligible: boolean;
}

/** The one gate every horizontal step downstream takes. */
export const isHorizontalWall = (
  draft: { wallBlocking?: WallBlocking } | null | undefined,
): boolean => draft?.wallBlocking?.blocking === "horizontal";

/** Whether a branch (first name the reading knows) carries row levels. A merchant-forced
 *  horizontal wall is their reading of the whole wall, so every branch's rows are read anew. */
export const branchStacksRows = (wall: WallBlocking, ...names: readonly string[]): boolean => {
  if (wall.blocking !== "horizontal") return false;
  if (wall.read !== "horizontal") return true;
  const known = names.find((name) => name in wall.branches);
  return known !== undefined && wall.branches[known] === true;
};

/** The frontend extends this with the placement offers a merchant kept in CDT Studio. */
export interface StudioDraft {
  /** Absent: the wall blocks vertically. Ask `isHorizontalWall`, never the field's presence. */
  wallBlocking?: WallBlocking;
  /** Horizontal walls only: how brands take shelves, as `CdtRuleSet.brandShelves` ships it.
   *  Absent: whole shelves where a brand's sales come close. */
  brandShelves?: "full" | "whereClose" | "sales";
  /** The ship-time measurement must replay without splitting its own bands again. */
  measuredBands?: { sectionAttr: string; strata: readonly StratumPlan[] };
  spine: readonly SpineLevel[];
  valueOrder: Readonly<Record<string, readonly string[]>>;
  /** Keyed by parent scope path (root = ""); wins over `valueOrder`. */
  scopedValueOrder: Readonly<Record<string, readonly string[]>>;
  /** A preference, not a fence: the pour falls back to neighbouring lanes, and no entry means a width-share slice. */
  bandOverrides: Readonly<Record<string, readonly number[]>>;
  orientOverrides: Readonly<Record<string, Orientation>>;
  /** Per-branch level order from this scope's depth down, present ONLY where a branch differs from
   *  `spine` (which stays the topological merge the vendored resolver reads). `collapse4` must
   *  resolve tiers through `levelsForScope`, or a ragged branch ships `cdt1 = ""`. */
  scopedLevelOrder?: Readonly<Record<string, readonly string[]>>;
  /** Used only while the SKU's node still covers those shelves. */
  shelfHints: Readonly<Record<string, readonly { shelf: number; hFacings: number }[]>>;
  attrOverrides: Readonly<Record<string, Readonly<Record<string, string | null>>>>;
  promotions?: readonly AppliedPromotion[];
  levelBands?: Readonly<Record<string, Readonly<Record<string, string>>>>;
  poolingOverrides?: Readonly<Record<string, "vertical" | "pooled">>;
  restrictions: readonly AcceptedRestriction[];
  dismissedFlagIds: readonly string[];
  /** Keyed by CONTENT-derived rule id — never a list index, which renumbers. */
  placementRuleDecisions?: Readonly<Record<string, "keep" | "decline">>;
  acceptedFlagIds?: readonly string[];
  appliedFixFlagIds: readonly string[];
}

export const PATH_SEPARATOR = " › ";

/** Values meaning "no data here". A placeholder holds NO POSITION in a level's order, so ranking,
 *  comparing and banding must SKIP it rather than treat it as a value of its own. A bare "NA" is
 *  not one: it can be a brand's name, and the adapter, which sees the product's name, already
 *  writes every missing "NA" as Unspecified. */
export const PLACEHOLDER_VALUE = /^(unspecified|n[/-]a|none|unknown|)$/i;

export const isPlaceholderValue = (value: string | null | undefined): boolean =>
  PLACEHOLDER_VALUE.test(String(value ?? "").trim());

/** Values that name no block: "Unassigned CDT2", "Others", "Unspecified". Wider than a placeholder,
 *  since a catch-all bucket is no brand or block the plan was asked to keep. */
const NO_BLOCK_VALUE = /^(unspecified|unassigned\b.*|others?|n[/-]?a|none|unknown|—|-)?$/i;

export const namesNoBlock = (value: string | null | undefined): boolean =>
  NO_BLOCK_VALUE.test(String(value ?? "").trim());

/** The level sequence a SCOPE splits by; overrides reorder levels, never rename them. Every
 *  positional tree walk must go through this or the parts will disagree about a branch's rungs. */
export const levelsForScope = (
  spine: readonly SpineLevel[],
  scopedLevelOrder: Readonly<Record<string, readonly string[]>> | undefined,
  path: readonly string[],
): readonly SpineLevel[] => {
  if (!scopedLevelOrder) return spine;
  /* Overrides COMPOSE, shallowest first: each reorders what its ancestors settled. */
  let levels = spine;
  for (let depth = 0; depth <= path.length; depth += 1) {
    const order = scopedLevelOrder[path.slice(0, depth).join(PATH_SEPARATOR)];
    if (!order) continue;
    const byAttr = new Map(levels.map((level) => [level.attr, level]));
    const reordered = order.flatMap((attr) => {
      const level = byAttr.get(attr);
      return level ? [level] : [];
    });
    levels = [...levels.slice(0, depth), ...reordered];
  }
  return levels;
};

export interface SplitScope<T> {
  level: SpineLevel;
  /** The values of the levels above that split, in the scope's own level order. */
  path: readonly string[];
  entries: readonly T[];
  groups: ReadonlyMap<string, readonly T[]>;
}

/** Visits every scope where a level splits its entries, keyed as the contract maps
 *  (`orientOverrides`, `scopedValueOrder`, `bandOverrides`) key it: a level holding one value in
 *  a scope adds nothing to the path. */
export const walkSplitScopes = <T>(
  entries: readonly T[],
  spine: readonly SpineLevel[],
  scopedLevelOrder: Readonly<Record<string, readonly string[]>> | undefined,
  valueOf: (entry: T, attr: string) => string,
  visit: (scope: SplitScope<T>) => void,
  levelIndex = 0,
  path: readonly string[] = [],
): void => {
  const levels = levelsForScope(spine, scopedLevelOrder, path);
  for (let index = levelIndex; index < levels.length && entries.length > 1; index += 1) {
    const level = levels[index];
    const groups = new Map<string, T[]>();
    for (const entry of entries) {
      const value = valueOf(entry, level.attr);
      const group = groups.get(value);
      if (group) group.push(entry);
      else groups.set(value, [entry]);
    }
    if (groups.size < 2) continue;
    visit({ level, path, entries, groups });
    for (const [value, group] of groups) {
      walkSplitScopes(group, spine, scopedLevelOrder, valueOf, visit, index + 1, [...path, value]);
    }
    return;
  }
};

/** Which path-keyed contract keys a carve renames. Each carved key is derived the way the POST-CARVE
 *  walk derives it: segments resolve down the branch own level sequence against the band members and
 *  survive only while their level still splits them. The parent one-segment key is never mapped. */
export const carvedKeyMapForPromotion = (
  items: readonly StudioItem[],
  draft: Pick<StudioDraft, "spine" | "attrOverrides"> &
    Partial<Pick<StudioDraft, "scopedLevelOrder" | "levelBands">>,
  carve: { parentValue: string; bandVariant: string; skuIds: readonly string[] },
  keys: readonly string[],
): Readonly<Record<string, string>> => {
  const band = new Set(carve.skuIds);
  const bandItems = items.filter((item) => band.has(item.skuId));
  if (bandItems.length === 0) return {};

  const attrsOf = new Map<string, Record<string, string | null>>();
  const attrOf = (item: StudioItem, attr: string): string => {
    let resolved = attrsOf.get(item.skuId);
    if (!resolved) {
      resolved = effectiveAttrs(item, draft);
      attrsOf.set(item.skuId, resolved);
    }
    return String(resolved[attr] ?? "");
  };
  const discriminates = (scope: readonly StudioItem[], attr: string): boolean => {
    const seen = new Set<string>();
    for (const item of scope) {
      const value = attrOf(item, attr);
      if (isPlaceholderValue(value)) continue;
      seen.add(value);
      if (seen.size >= 2) return true;
    }
    return false;
  };

  const out: Record<string, string> = {};
  for (const key of keys) {
    if (key in out) continue;
    const segments = key.split(PATH_SEPARATOR);
    if (segments[0] !== carve.parentValue || segments.length < 2) continue;

    let scope = bandItems;
    let levelIndex = 1;
    let belongs = true;
    const kept: string[] = [carve.bandVariant];
    for (let index = 1; index < segments.length; index += 1) {
      const levels = levelsForScope(draft.spine, draft.scopedLevelOrder, segments.slice(0, index));
      let matched: StudioItem[] | null = null;
      while (levelIndex < levels.length) {
        const attr = levels[levelIndex].attr;
        levelIndex += 1;
        const filtered = scope.filter((item) => attrOf(item, attr) === segments[index]);
        if (filtered.length > 0) {
          if (discriminates(scope, attr)) kept.push(segments[index]);
          matched = filtered;
          break;
        }
      }
      if (!matched) {
        belongs = false;
        break;
      }
      scope = matched;
    }
    if (belongs) out[key] = kept.join(PATH_SEPARATOR);
  }
  return out;
};

/** The carved ROOT inherits the PARENT entry when no band branch supplied one; the parent keeps its own. */
export const rekeyScopedLevelOrderForPromotion = (
  scopedLevelOrder: Readonly<Record<string, readonly string[]>> | undefined,
  keyMap: Readonly<Record<string, string>>,
  parentValue: string,
  bandVariant: string,
): Readonly<Record<string, readonly string[]>> | undefined => {
  if (!scopedLevelOrder) return undefined;
  let out = rekeyScopedMapForPromotion(scopedLevelOrder, keyMap);
  const parentOrder = scopedLevelOrder[parentValue];
  if (parentOrder && !(bandVariant in out)) out = { ...out, [bandVariant]: parentOrder };
  return Object.keys(out).length > 0 ? out : undefined;
};

export const dropScopedLevelOrderForPromotion = (
  scopedLevelOrder: Readonly<Record<string, readonly string[]>> | undefined,
  bandVariant: string,
): Readonly<Record<string, readonly string[]>> | undefined => {
  if (!scopedLevelOrder) return undefined;
  const out = dropScopedMapForPromotion(scopedLevelOrder, bandVariant);
  return Object.keys(out).length > 0 ? out : undefined;
};

/** A promotion is a RENAME, NOT A RE-MEASUREMENT, so the SKUs under the carved key are the ones the
 *  entry was measured on. Returns the SAME reference when nothing carries. */
export const rekeyScopedMapForPromotion = <T>(
  map: Readonly<Record<string, T>>,
  keyMap: Readonly<Record<string, string>>,
): Readonly<Record<string, T>> => {
  const carved: Record<string, T> = {};
  for (const [key, carvedKey] of Object.entries(keyMap)) {
    if (!(key in map)) continue;
    if (carvedKey in map || carvedKey in carved) continue;
    carved[carvedKey] = map[key];
  }
  return Object.keys(carved).length > 0 ? { ...map, ...carved } : map;
};

export const dropScopedMapForPromotion = <T>(
  map: Readonly<Record<string, T>>,
  bandVariant: string,
): Readonly<Record<string, T>> => {
  if (!Object.keys(map).some((key) => key.split(PATH_SEPARATOR)[0] === bandVariant)) return map;
  const out: Record<string, T> = {};
  for (const [key, value] of Object.entries(map)) {
    if (key.split(PATH_SEPARATOR)[0] === bandVariant) continue;
    out[key] = value;
  }
  return out;
};

/** An item's SLAB PATH: the leading run of internal-"horizontal" levels along the item's OWN
 *  branch, clamped to at least one and at most three; `bandAttr` is the level directly below it.
 *  Block identity must come through this, not a positional `spine.slice`. */
export const slabPathOfAttrs = (
  spine: readonly SpineLevel[],
  scopedLevelOrder: Readonly<Record<string, readonly string[]>> | undefined,
  attrs: Record<string, string | null>,
): { values: readonly string[]; bandAttr: string | null } => {
  const values: string[] = [];
  let levels = levelsForScope(spine, scopedLevelOrder, values);
  while (values.length < 3) {
    const level = levels[values.length];
    if (!level || level.orient !== "horizontal") break;
    values.push(String(attrs[level.attr] ?? ""));
    levels = levelsForScope(spine, scopedLevelOrder, values);
  }
  if (values.length === 0 && levels.length > 0) {
    values.push(String(attrs[levels[0].attr] ?? ""));
    levels = levelsForScope(spine, scopedLevelOrder, values);
  }
  return { values, bandAttr: levels[values.length]?.attr ?? null };
};

export const effectiveAttrs = (
  item: StudioItem,
  draft: Pick<StudioDraft, "attrOverrides"> & Partial<Pick<StudioDraft, "levelBands">>,
): Record<string, string | null> => {
  const override = draft.attrOverrides[item.skuId];
  const bands = draft.levelBands;
  // No banded level: return the item's own attrs BY REFERENCE, so nothing downstream sees a copy.
  if (!bands || Object.keys(bands).length === 0) {
    return override ? { ...item.attrs, ...override } : item.attrs;
  }
  const banded: Record<string, string | null> = { ...item.attrs, ...override };
  for (const [attr, bandOf] of Object.entries(bands)) {
    const raw = banded[attr];
    if (raw == null) continue;
    const band = bandOf[raw];
    if (band !== undefined) banded[attr] = band;
  }
  return banded;
};

export interface GeneratedSegment {
  run: number;
  shelf: number;
  xCm: number;
  hFacings: number;
}

export interface GeneratedPlacement {
  skuId: string;
  segments: readonly GeneratedSegment[];
}

export interface GeneratedLayout {
  placements: Readonly<Record<string, GeneratedPlacement>>;
  walkOrder: readonly string[];
  shedFacings?: readonly {
    skuId: string;
    run: number;
    shelf: number;
    requested: number;
    placed: number;
  }[];
  ceilingViolations?: readonly {
    skuId: string;
    run: number;
    shelf: number;
    heightCm: number;
    openingCm: number;
  }[];
}

/** A layout re-expressed on the basis the RENDERER draws. Segment `xCm` is an ANCHOR, not a
 *  position (`layoutToPog` re-derives every final x), so any report of what the plan did must be
 *  measured through a projection. The identity projection means "contract basis". */
export type LayoutProjection = (layout: GeneratedLayout) => GeneratedLayout;

export type FlagKind =
  | "orphan"
  | "split"
  | "ragged"
  | "contiguity"
  | "column"
  | "data"
  | "inversion"
  | "size-order"
  | "he-order"
  | "flavour-order"
  | "ambiguous"
  | "singleton";

export type FlagSeverity = "blocking" | "toCheck" | "toReview";

export const FLAG_SEVERITY: Record<FlagKind, FlagSeverity> = {
  orphan: "blocking",
  split: "blocking",
  ragged: "toCheck",
  contiguity: "blocking",
  // A column break has NO SAFE REPAIR YET, so it is reported for a human: the resolver treats it as
  // an orphan-fragment pin and relocates the whole lower band, splitting more blocks than it fixes.
  column: "toCheck",
  data: "blocking",
  inversion: "toCheck",
  "size-order": "toCheck",
  "he-order": "toCheck",
  "flavour-order": "toReview",
  ambiguous: "toReview",
  singleton: "toReview",
};

export interface FlagEvidence {
  built?: readonly string[];
  want?: readonly string[];
  notes?: readonly string[];
}

export type FixAction =
  | { type: "reorderValues"; attr: string; scopeKey: string; order: readonly string[] }
  | { type: "retagItems"; attr: string; value: string | null; skuIds: readonly string[] };

export interface Flag {
  id: string;
  kind: FlagKind;
  levelLabel?: string;
  severity: FlagSeverity;
  scopePath: readonly string[];
  title: string;
  detail: string;
  skuIds: readonly string[];
  anchorSkuId: string;
  evidence: FlagEvidence;
  fix: FixAction | null;
  fixLabel: string | null;
}

export type RestrictionKind = "overlay" | "breakout" | "split-off" | "shelf-rule";

/** The INTERNAL underscored grammar (`toVariantRestrictionsFileCsv` spells these with spaces on the
 *  way out). The kind cannot imply the condition: one `shelf-rule` is a fence, another reserves. */
export type RestrictionCondition = "CAN_HAVE" | "MUST_HAVE" | "MUST_HAVE_ONLY" | "MUST_NOT_HAVE";

export interface RestrictionCell {
  /** 1-based shelf rank (1 = top, matching the endpoint's "Top"). */
  shelf: number;
  /** 1-based bay numbers (the fixture's `section_num`), ascending. */
  bays: readonly number[];
}

/** PER SHELF: the endpoint takes `bay_no` as a "|"-separated list and `shelf` as ONE term, so a row
 *  is a (shelf, bay-list) pair. Unioning bays across shelves evicts siblings from cells. */
export interface RestrictionPlacement {
  cells: readonly RestrictionCell[];
}

export interface AcceptedRestriction {
  id: string;
  kind: RestrictionKind;
  cdtPath: readonly string[];
  collapsedKey: string | null;
  /** Measured off the SKUs' own placements at finalize time. null = not determinable and NOT
   *  uploaded: a wildcard row is a category-wide outage, a missing row is inert. */
  placement: RestrictionPlacement | null;
  /** Absent keeps the kind-derived mapping (`CAN_HAVE` for split-off and breakout, else `MUST_HAVE`). */
  condition?: RestrictionCondition;
  skuIds: readonly string[];
  source: { origin: "inferred" | "user" | "accepted-violation"; flagId?: string };
  text: string;
}

export interface BrokenBlock {
  node: string;
  foreignFacings: number;
  lanes: readonly string[];
  /** true = introduced by the current draft, false = present in the base plan. */
  introduced: boolean;
}

export interface Provenance {
  broken: readonly BrokenBlock[];
  newBreaks: number;
  preExistingBreaks: number;
  movedSkuIds: readonly string[];
}

export interface CollapsedLevel {
  attr: string;
  label: string;
  sourceAttrs: readonly string[];
  orient: Orientation;
}

export interface CollapsedContract {
  levels: readonly CollapsedLevel[];
  valueOrder: Readonly<Record<string, readonly string[]>>;
  itemValues: Readonly<Record<string, readonly string[]>>;
}

export interface SkuAssignment {
  skuId: string;
  upc: string;
  name: string;
  cdt0: string;
  cdt1: string;
  cdt2: string;
  cdt3: string;
  cdt1Priority: number;
  cdt2Priority: number;
  cdt3Priority: number;
}

export interface LayoutDiff {
  skuId: string;
  before: string;
  after: string;
}

export interface FinalizeResult {
  contract: CollapsedContract;
  verification: {
    identical: boolean;
    orderPreserved: boolean;
    diffs: readonly LayoutDiff[];
  };
  assignments: readonly SkuAssignment[];
  restrictions: readonly AcceptedRestriction[];
  /** Dropped: their CDT0 value is not one this contract ships. Reported, not deleted, so it can be re-made. */
  staleRestrictions: readonly { restriction: AcceptedRestriction; reason: string }[];
}
